import { crearStorage } from '../../lib/localforage'
import _ from 'lodash'
import * as z from 'zod'
import { LocalNotifications } from '@capacitor/local-notifications'
import type { ActionPerformed, PushNotificationSchema, Token } from '@capacitor/push-notifications'
import { PushNotifications as CapacitorPushNotifications } from '@capacitor/push-notifications'

import { ErrorAumentado } from '@comun/errorAumentado'
import { NotificacionAumentadaZod, type NotificacionAumentada } from '@comun/tipos/notificaciones'
import { HashJson } from '@comun/hashU'
import { consolo } from '../../composables/consolo'
import emisorEventosObjeto from '../../lib/emisorEventosObjeto'
import { configNotificaciones, getConfigNotificaciones } from './config'

const notiCache = crearStorage('notiStore')

// ==========================================
// Helper para manejo de errores robusto
// ==========================================

async function ejecutarConManejoDeError<T>(
  nombreFuncion: string,
  operacion: () => Promise<T>,
  fallback?: T
): Promise<T | undefined> {
  try {
    return await operacion()
  } catch (error) {
    consolo.error('[nativas]', `${nombreFuncion}: Error ejecutando operacion`, error)
    if (fallback !== undefined) {
      return fallback
    }
    return undefined
  }
}

// ==========================================
// Detección de plataforma
// ==========================================

const esIOS = computed(() => configNotificaciones.value?.dispositivo === 'ios')
const esAndroid = computed(() => configNotificaciones.value?.dispositivo === 'android')

const tokenDestinatarioRef = ref<string | null>(null)
export const tokenDestinatario = computed(() => unref(tokenDestinatarioRef))

let _suscribirmePromesa: Promise<boolean | undefined> | null = null // Lista Única

async function suscribirme(token: string) {
  if (_suscribirmePromesa) {
    consolo.log('[nativas]', 'suscribirme', 'Lista Única: suscripción en proceso, esperando...')
    return _suscribirmePromesa
  }
  _suscribirmePromesa = _ejecutarSuscribirme(token)
  try {
    return await _suscribirmePromesa
  } finally {
    _suscribirmePromesa = null
  }
}

async function _ejecutarSuscribirme(token: string) {
  const fx = 'suscribirme'
  consolo.info('[nativas]', fx, 'Token recibido:', token?.substring(0, 20) + '...')

  try {
    const bodyData = { token }
    const headers = await HeadersBase()
    const url = `${getConfigNotificaciones().apiUrl}/notificador/suscribir`

    consolo.log('[nativas]', fx, 'URL:', url)

    const respuesta = await comoSiFueraPorAxios({
      url,
      method: 'post',
      headers,
      data: bodyData
    })

    consolo.log('[nativas]', fx, 'Respuesta cruda:', respuesta)

    const parseo = z.discriminatedUnion('ok', [
      z.object({
        ok: z.literal(true)
      }),
      z.object({
        ok: z.literal(false),
        error: z.string()
      })
    ]).safeParse(respuesta, { reportInput: true })

    if (!parseo.success)
      throw new ErrorAumentado(fx, { error: parseo.error })
    if (!parseo.data.ok)
      throw new ErrorAumentado(fx, { error: parseo.data.error })

    consolo.info('[nativas]', fx, '✅ Suscripción exitosa')
    return parseo.data.ok
  }
  catch (e) {
    consolo.error('[nativas]', fx, '❌ ERROR en suscribirme:', e)
    if (e instanceof ErrorAumentado)
      throw e.trazar(fx)
    consolo.error('[nativas]', 'error', e)
    throw new ErrorAumentado(fx, { error: e })
  }
}

async function inicializarReceptorPushNativo() {
  consolo.info('[nativas]', 'inicializarReceptorPushNativo', 'Configurando listeners de CapacitorPushNotifications')

  // On success, we should be able to receive notifications
  CapacitorPushNotifications.addListener('registration', (token: Token) => {
    consolo.info('[nativas]', 'registration', 'Token recibido:', token.value?.substring(0, 20) + '...')
    tokenDestinatarioRef.value = token.value
    suscribirme(token.value)
  })

  // Some issue with our setup and push will not work
   
  CapacitorPushNotifications.addListener('registrationError', (error: unknown) => {
    consolo.error('[nativas]', `PUSH: Error en registro: ${JSON.stringify(error)}`)
  })

  // Show us the notification payload if the app is open on our device
  CapacitorPushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    consolo.info('[nativas]', `PUSH: Notificación recibida con app abierta`)
    const rawData = notification.data
    if (!rawData?.notificacion) return

    let parsed: unknown
    try {
      parsed = JSON.parse(rawData.notificacion)
    } catch (e) {
      consolo.error('[nativas]', 'PUSH: Error parseando notificacion JSON', e)
      return
    }

    const resultado = NotificacionAumentadaZod.safeParse(parsed)
    if (!resultado.success) {
      consolo.warn('[nativas]', 'PUSH: Notificación inválida al recibir', resultado.error.issues)
      return
    }

    ConectorNativo.emit('notificacionRecibida', resultado.data)
  })

  // Method called when tapping on a notification
  CapacitorPushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed: ActionPerformed) => {
    consolo.info('[nativas]', `PUSH: Acción de notificación ejecutada`)
    const rawData = actionPerformed.notification.data

    if (!rawData?.notificacion) {
      consolo.warn('[nativas]', 'PUSH: No hay campo notificacion en data del tap')
      return
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(rawData.notificacion)
    } catch (e) {
      consolo.error('[nativas]', 'PUSH: Error parseando notificacion JSON en tap', e)
      return
    }

    const resultado = NotificacionAumentadaZod.safeParse(parsed)
    if (!resultado.success) {
      consolo.error('[nativas]', 'PUSH: Error validando notificación en tap', resultado.error.issues)
      return
    }

    const notificacion = resultado.data

    ConectorNativo.emit('notificationClick', notificacion)

    const instanciaID = notificacion.data?.instanciaID as string | undefined
    if (instanciaID) {
      // * Abrir modal de la instancia
      EventosDeAppAPI.emitirEventoDeApp('instancia:abrirModal', { instanciaID })
    }
  })

  // Ejemplo de notificación:
  // PUSH: Push action performed:
  // const ejemplo = {
  //   "actionId": "tap",
  //   "notification": {
  //     "title": "Se acabó la espera, hay una vacante!",
  //     "subtitle": "",
  //     "id": "F5E791D4-D565-4D60-9544-3856871B2892",
  //     "body": "Tienes unos segundos de acceso prioritario para agendar la sesión.",
  //     "data": {
  //       "google.c.a.e": "1",
  //       "google.c.fid": "dCDjJ4y4K0BnrLmD144h3B",
  //       "gcm.message_id": "1719555824697527",
  //       "tipo": "prioridadPorEspera",
  //       "aps": {
  //         "alert": {
  //           "body": "Tienes unos segundos de acceso prioritario para agendar la sesión.",
  //           "title": "Se acabó la espera, hay una vacante!"
  //         }
  //       },
  //       "instanciaID": "i2024-06-29>oV4dgEgLr2>zd4RMo2bL7",
  //       "solicitudID": "07ZwxaLOLS", "google.c.sender.id": "544236061744"
  //     }, "badge": 1
  //   }
  // }

  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting
  try {
    const result = await CapacitorPushNotifications.checkPermissions()
    if (result.receive === 'granted') {
      await CapacitorPushNotifications.register()
    }
  } catch (error) {
    consolo.error('[nativas]', 'Error en checkPermissions:', error)
  }
}

async function pedirPermisoPush(): Promise<boolean> {
  const fx = 'pedirPermisoPush'
  try {
    const result = await CapacitorPushNotifications.requestPermissions()
    if (result.receive === 'granted') {
      await CapacitorPushNotifications.register()
      return true
    }
    return false
  }
  catch (e) {
    consolo.error('[nativas]', fx, 'Error solicitando permisos push:', e)
    return false
  }
}



import type { InterfazNotificaciones } from './interface'
import type { PermissionStatus } from '@capacitor/local-notifications'

// ... imports

// Keep obtenerNuevoID outside the exported object or cast it, 
// but since we defined the type explicitly, we should probably remove internal helpers from the public object 
// or extend the interface. Better to move helper out or keep it but cast.
// Let's move obtenerNuevoID to a standalone function or just ignore the type check for now by not typing the variable strictly 
// OR better: make InterfazNotificaciones extendable or just fix the object structure.

async function obtenerNuevoID() {
  let idPrevio = (await notiCache.getItem<number>('ultimoID')) || 0
  if (Number.isNaN(idPrevio)) idPrevio = 0
  await notiCache.setItem<number>('ultimoID', idPrevio + 1)
  return idPrevio + 1
}

export const ConectorNativo: InterfazNotificaciones = {
  ...emisorEventosObjeto,

  async init() {
    const fx = 'ConectorNativo.init'
    const notificacionesPermitidas = await ConectorNativo.leerPermiso()
    consolo.info('[nativas]', fx, 'Permiso inicial:', notificacionesPermitidas)
    ConectorNativo.emit('cambioPermiso', notificacionesPermitidas)

    inicializarReceptorPushNativo()
    consolo.info('[nativas]', fx, '✅ Adaptador nativo inicializado')
    LocalNotifications.removeAllListeners()
    LocalNotifications.addListener('localNotificationReceived', (n: unknown) => {
      // Notificación local recibida
    })
    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (a: unknown) => {
        const data = _.get(a, 'notification.extra')
        ConectorNativo.emit('notificationClick', data)
      }
    )
  },
  async suscribir() {
    const fx = 'ConectorNativo.suscribir'
    const tokenAlmacenado = unref(tokenDestinatarioRef)

    if (tokenAlmacenado) {
      // Token ya obtenido previamente — registrar en backend directamente
      consolo.info('[nativas]', fx, 'Token existente, suscribiendo...')
      await suscribirme(tokenAlmacenado)
      return
    }

    const permiso = await ConectorNativo.leerPermiso()
    if (permiso) {
      consolo.info('[nativas]', fx, 'Registrando push con permiso concedido')
      await CapacitorPushNotifications.register()
    } else {
      consolo.warn('[nativas]', fx, 'No hay permiso, no se puede registrar')
    }
  },
  async leerPermiso(): Promise<boolean> {
    const estado = await this.obtenerEstadoPermiso()
    return estado === 'granted'
  },
  async obtenerEstadoPermiso(): Promise<'granted' | 'denied' | 'prompt'> {
    const fx = 'ConectorNativo>obtenerEstadoPermiso'

    // En iOS, usar PushNotifications para permisos ya que LocalNotifications no está implementado
    if (unref(esIOS)) {
      return await ejecutarConManejoDeError(
        `${fx} [iOS]`,
        async () => {
          const resultado = await CapacitorPushNotifications.checkPermissions()
          return resultado.receive as 'granted' | 'denied' | 'prompt'
        },
        'prompt' as const
      ) ?? 'prompt'
    }

    // En Android: usar LocalNotifications
    return await ejecutarConManejoDeError(
      `${fx} [Android/Local]`,
      async () => {
        const resultado = await LocalNotifications.checkPermissions()
        return resultado.display as 'granted' | 'denied' | 'prompt'
      },
      'prompt' as const
    ) ?? 'prompt'
  },
  async pedirPermiso(): Promise<boolean> {
    const fx = 'ConectorNativo>pedirPermiso'

    try {
      // En iOS, usamos PushNotifications para permisos ya que maneja automáticamente
      // el registro APNs y es más confiable para la primera solicitud de permisos
      // LocalNotifications también funciona en iOS, pero usamos este enfoque híbrido
      if (unref(esIOS)) {

        // Solicitar permisos via PushNotifications
        const permisoPush = await ejecutarConManejoDeError(
          `${fx} [iOS Push]`,
          async () => {
            const resultado = await CapacitorPushNotifications.requestPermissions()

            if (resultado.receive === 'granted') {
              await CapacitorPushNotifications.register()
            }

            return resultado.receive === 'granted'
          },
          false
        )

        if (permisoPush === undefined) {
          consolo.error('[nativas]', fx, 'Error solicitando permisos Push en iOS')
          this.emit('error', { tipo: 'permiso', mensaje: 'Error solicitando permisos de notificaciones' })
          return false
        }

        this.emit('cambioPermiso', permisoPush ?? false)
        return permisoPush ?? false
      }

      // En Android: mantener comportamiento actual pero con mejor manejo de errores

      // Paso 1: Solicitar permisos de Push
      const pushExitoso = await ejecutarConManejoDeError(
        `${fx} [Android Push]`,
        pedirPermisoPush,
        false
      )

      if (!pushExitoso) {
        consolo.warn('[nativas]', fx, 'Falló la solicitud de permisos Push, continuando con LocalNotifications')
      }

      // Paso 2: Solicitar permisos de LocalNotifications
      const permisoLocal = await ejecutarConManejoDeError(
        `${fx} [Android Local]`,
        async () => {
          const resultado = await LocalNotifications.requestPermissions()
          return resultado.display === 'granted'
        },
        false
      ) ?? false

      this.emit('cambioPermiso', permisoLocal)
      return permisoLocal

    } catch (error) {
      consolo.error('[nativas]', fx, 'Error inesperado solicitando permisos:', error)
      this.emit('error', { tipo: 'permiso', mensaje: 'Error inesperado', error })
      return false
    }
  },

  async programar(notificacion: NotificacionAumentada): Promise<boolean> {
    const fx = 'ConectorNativo>programar'

    // Validar notificacion
    if (!notificacion.titulo || !notificacion.fechaEnQueMostrar) {
      consolo.error('[nativas]', fx, 'Notificación inválida: falta título o fecha')
      return false
    }

    // Verificar permisos antes de programar
    const permisosOk = await this.leerPermiso()
    if (!permisosOk) {
      consolo.warn('[nativas]', fx, 'No hay permisos de notificación, omitiendo programación')
      return false
    }

    const resultado = await ejecutarConManejoDeError(
      fx,
      async () => {
        const id = await obtenerNuevoID()
        await LocalNotifications.schedule({
          notifications: [{
            title: notificacion.titulo,
            body: notificacion.texto || '',
            id,
            schedule: { at: notificacion.fechaEnQueMostrar },
            extra: {
              ...notificacion.data,
              texto: notificacion.texto,
              url: notificacion.urlQueAbrir
            }
          }]
        })
        consolo.log(fx, 'Notificacion programada exitosamente, ID:', id)
        return true
      },
      false
    )

    if (!resultado) {
      consolo.error(fx, 'Error programando notificacion')
    }

    return resultado ?? false
  },

  async programarSoloEstasNotificaciones(notificaciones: NotificacionAumentada[]): Promise<{ exitosos: number; fallidos: number }> {
    const fx = 'ConectorNativo>programarSoloEstasNotificaciones'
    consolo.log(fx, `Programando ${notificaciones.length} notificaciones`)

    if (notificaciones.length === 0) {
      consolo.log(fx, 'No hay notificaciones para programar')
      return { exitosos: 0, fallidos: 0 }
    }

    // Verificar permisos antes de programar
    const permisosOk = await this.leerPermiso()
    if (!permisosOk) {
      consolo.warn(fx, 'No hay permisos de notificacion, omitiendo programacion')
      return { exitosos: 0, fallidos: notificaciones.length }
    }

    const idBase = await obtenerNuevoID()
    let exitosos = 0
    let fallidos = 0

    await Promise.all(lodash.map(notificaciones, async (notificacion, index) => {
      const resultado = await ejecutarConManejoDeError(
        `${fx} [${index}]`,
        async () => {
          // Validar notificacion
          if (!notificacion.titulo || !notificacion.fechaEnQueMostrar) {
            consolo.error(`${fx} [${index}]`, 'Notificacion invalida')
            return false
          }

          await LocalNotifications.schedule({
            notifications: [{
              title: notificacion.titulo,
              body: notificacion.texto || '',
              id: idBase + index,
              schedule: { at: notificacion.fechaEnQueMostrar },
              extra: {
                ...notificacion.data,
                texto: notificacion.texto,
                url: notificacion.urlQueAbrir
              }
            }]
          })
          return true
        },
        false
      )

      if (resultado) {
        exitosos++
      } else {
        fallidos++
      }
    }))

    consolo.log(fx, `Resultado: ${exitosos} exitosos, ${fallidos} fallidos`)
    return { exitosos, fallidos }
  },

  async cancelarTodas(): Promise<boolean> {
    const fx = 'ConectorNativo>cancelarTodas'
    consolo.log(fx)

    const resultado = await ejecutarConManejoDeError(
      fx,
      async () => {
        const { notifications } = await LocalNotifications.getPending()
        if (_.isEmpty(notifications)) {
          consolo.log(fx, 'No hay notificaciones pendientes para cancelar')
          return true
        }
        await LocalNotifications.cancel({ notifications })
        consolo.log(fx, `${notifications.length} notificaciones canceladas`)
        return true
      },
      false
    )

    return resultado ?? false
  }
}

